import React, { useEffect, useState } from 'react';
import { reduxForm, Field } from 'redux-form';
import { withTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import { addDays } from 'date-fns';
import RenderField from '../../../shared/components/form/RenderField';
import SmartButton from '../../../shared/components/SmartButton';
import DateField from '../../../shared/components/form/DateField';
import Select from '../../../shared/components/form/Select';
import ScheduleSelector from './SchoolDaySelector';
import ColorPicked from './ColorPicked';
import { TYPE_MODAL } from '../../../shared/utils/constants';
import validate from './Validate';
import SmartCard from '../../../shared/components/SmartCard';

const FullScreenForm = (props) => {
  const {
    t,
    content,
    initialize,
    calendarMarkers,
    handleSubmit,
    selectedTerm,
    calendarCycles,
  } = props;
  const [formModal, setForm] = useState({});
  const [validDate, setValidDate] = useState({});

  const checkFormTypeCycle = (values) => {
    if (values.type === TYPE_MODAL.CYCLE && calendarCycles.length) {
      const index = calendarCycles.findIndex(
        (cycle) => cycle.uuid === values.uuid
      );
      let beforeCycle;
      let afterCycle;
      let updatedValidDate;
      if (calendarCycles.length > 1) {
        beforeCycle = calendarCycles[index - 1];
        afterCycle = calendarCycles[index + 1];
        if (beforeCycle && afterCycle) {
          updatedValidDate = {
            beginDate: addDays(beforeCycle.endInMilli, 1),
            endDate: addDays(afterCycle.beginInMilli, -1),
          };
        } else if (afterCycle) {
          updatedValidDate = {
            beginDate: selectedTerm.initialDate,
            endDate: afterCycle.beginInMilli
              ? addDays(afterCycle.beginInMilli, -1)
              : selectedTerm.finalDate,
          };
        } else if (beforeCycle) {
          updatedValidDate = {
            beginDate: beforeCycle.endInMilli
              ? addDays(beforeCycle.endInMilli, 1)
              : selectedTerm.initialDate,
            endDate: selectedTerm.finalDate,
          };
        }
      } else {
        updatedValidDate = {
          beginDate: selectedTerm.initialDate,
          endDate: selectedTerm.finalDate,
        };
      }
      setValidDate(updatedValidDate);
    }
  };

  const onChangeValuesForm = (field, value) => {
    const updatedForm = { ...formModal };
    updatedForm[field] = value;
    setForm(updatedForm);
  };

  useEffect(() => {
    initialize(content);
    setForm(content);
  }, [content]);

  useEffect(() => {
    initialize(formModal);
    checkFormTypeCycle(formModal);
  }, [formModal]);

  return (
    <SmartCard>
      <form onSubmit={handleSubmit} className="form ml-4 mr-4">
        {content.type !== TYPE_MODAL.MARKING ? (
          <>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.title')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name={content.type === TYPE_MODAL.EVENT ? 'title' : 'name'}
                  type="text"
                  component={RenderField}
                  placeholder={t('general.placeholders.title')}
                  onChange={({ target }) =>
                    onChangeValuesForm(
                      content.type === TYPE_MODAL.EVENT ? 'title' : 'name',
                      target.value
                    )
                  }
                />
              </div>
            </div>
            <div className="form__form-group--2_columns">
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.initial_date')}
                </span>
                <div className="form__form-group-field">
                  <Field
                    name="beginInMilli"
                    component={DateField}
                    minDate={validDate.beginDate}
                    maxDate={validDate.endDate}
                    placeholder={t('general.placeholders.select-date')}
                    onChange={(value) =>
                      onChangeValuesForm(
                        'beginInMilli',
                        new Date(value).getTime()
                      )
                    }
                  />
                </div>
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.final_date')}
                </span>
                <div className="form__form-group-field">
                  <Field
                    name="endInMilli"
                    component={DateField}
                    value={new Date()}
                    minDate={formModal.beginInMilli || selectedTerm.initialDate}
                    maxDate={validDate.endDate}
                    placeholder={t('general.placeholders.select-date')}
                    onChange={(value) =>
                      onChangeValuesForm(
                        'endInMilli',
                        new Date(value).getTime()
                      )
                    }
                  />
                </div>
              </div>
            </div>
            {content.type === TYPE_MODAL.EVENT && (
              <>
                <div className="form__form-group">
                  <span className="form__form-group-label">
                    {t('general.description')}
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="description"
                      component={RenderField}
                      type="textarea"
                      placeholder={t('general.placeholders.type-here')}
                      onChange={({ target }) =>
                        onChangeValuesForm('description', target.value)
                      }
                    />
                  </div>
                </div>
                <div className="form__form-group">
                  <span className="form__form-group-label">
                    {t('general.select-marking-to-assign')}
                  </span>
                  <div className="form__form-group-field">
                    <Field
                      name="marker"
                      component={Select}
                      isDisabled={!formModal.newMarker}
                      options={calendarMarkers.map((marker) => ({
                        ...marker,
                        label: marker.name,
                        value: marker.uuid,
                      }))}
                      placeholder={t('general.placeholders.select')}
                      onChange={(value) => onChangeValuesForm('marker', value)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.name')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="name"
                  type="text"
                  maxLength="150"
                  component={RenderField}
                  placeholder={t('general.placeholders.name')}
                  onChange={({ target }) =>
                    onChangeValuesForm('name', target.value)
                  }
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.is-school-day')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="schoolDay"
                  component={ScheduleSelector}
                  onChange={(value) => onChangeValuesForm('schoolDay', value)}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.marking-color')}
              </span>
              <div className="form_form-group-field modal-field pr-3">
                <Field
                  name="color"
                  component={ColorPicked}
                  onHandleChange={(value) => onChangeValuesForm('color', value)}
                  colorSelected={formModal.color}
                />
              </div>
            </div>
          </>
        )}
        <SmartButton color="success" label={t('general.save')} type="submit" />
      </form>
    </SmartCard>
  );
};

FullScreenForm.propTypes = {
  t: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  content: PropTypes.shape(PropTypes.shape).isRequired,
  calendarMarkers: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  calendarCycles: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  selectedTerm: PropTypes.shape(PropTypes.shape).isRequired,
};

export default reduxForm({
  form: 'AcademicCalendarFullForm',
  validate,
})(withTranslation('common')(FullScreenForm));
